import { useEffect, useMemo, useState } from "react";
import { api, getApiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { fmtDate } from "@/lib/format";
import { FileText, Download, Search } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["all", "passport", "visa", "medical_report", "insurance", "prescription", "invoice", "general"];

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const url = cat === "all" ? "/documents" : `/documents?category=${cat}`;
        const { data } = await api.get(url); setDocs(data);
      } catch (e) { toast.error(getApiErrorMessage(e)); }
      finally { setLoading(false); }
    })();
  }, [cat]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return docs.filter((d) => !s || (d.name || "").toLowerCase().includes(s));
  }, [docs, search]);

  const download = (id) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}`, "_blank");

  return (
    <div data-testid="documents-page">
      <PageHeader
        eyebrow="Records"
        title="Documents"
        subtitle="All patient and lead attachments — passports, visas, medical reports, invoices."
      />
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E9992]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search documents…"
            data-testid="docs-search"
            className="w-full pl-9 pr-3 py-2.5 border border-[#E8E6E1] rounded-full bg-white text-sm focus:outline-none focus:border-[#2A4D3E]" />
        </div>
        <div className="flex gap-1 bg-white border border-[#E8E6E1] rounded-full p-1 flex-wrap">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`doc-cat-${c}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition ${cat === c ? "bg-[#2A4D3E] text-white" : "text-[#5C6660] hover:bg-[#F7F5F2]"}`}>
              {c.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>

      {loading ? <div className="text-sm text-[#5C6660]">Loading…</div> : (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#FDFCFB] border-b border-[#E8E6E1] text-xs font-semibold uppercase tracking-wider text-[#5C6660] text-left">
              <tr>
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Category</th>
                <th className="px-5 py-3">Size</th>
                <th className="px-5 py-3">Uploaded</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr key={d.id} className="border-b border-[#E8E6E1] last:border-0 hover:bg-[#F7F5F2]" data-testid={`doc-row-${d.id}`}>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-[#E6F0EB] flex items-center justify-center"><FileText size={14} className="text-[#2A4D3E]" /></div>
                      <div className="text-sm font-medium text-[#2A332C]">{d.name}</div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-xs text-[#5C6660] capitalize">{(d.category || "").replace("_", " ")}</td>
                  <td className="px-5 py-3.5 text-xs text-[#5C6660]">{((d.size || 0) / 1024).toFixed(0)} KB</td>
                  <td className="px-5 py-3.5 text-xs text-[#8E9992]">{fmtDate(d.uploaded_at)}</td>
                  <td className="px-5 py-3.5">
                    <button onClick={() => download(d.id)} className="inline-flex items-center gap-1.5 text-xs text-[#2A4D3E] font-semibold hover:underline" data-testid={`doc-download-${d.id}`}>
                      <Download size={13} /> Download
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="5" className="px-5 py-12 text-center text-sm text-[#8E9992]">No documents found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
